import { useTranslation } from 'react-i18next';
import { differenceInMinutes, differenceInSeconds } from 'date-fns';
import getDurationFormatted from '@medflex/utils/getDurationFormatted';
import { USER_TYPES, CHAT_MESSAGE_TYPE } from '@medflex/utils/constants';
import { RequestTypes } from '@medflex/shared-types';
import { format } from '@medflex/i18n';
import useMergedMessages from './useMergedMessages';

const useChatMessagesExport = () => {
  const messages = useMergedMessages();
  const { t } = useTranslation(['global', 'components']);

  const concatFileNames = files =>
    `(${t('useChatMessagesExport.attachment', { count: files.length, ns: 'global' })}: ${files
      .map(({ name, duration }) => `${name} (${getDurationFormatted(duration)})`)
      .join(', ')})`;

  let msgsExported = '';

  messages.forEach(m => {
    if (
      m.type === USER_TYPES.PATIENT ||
      (m.createdAt && (!m.establishedAt || !m.endedAt)) ||
      m.deletedAt
    ) {
      return;
    }

    const fullNameSender = m.user.fullNameWithTitle || 'unknown';
    const time = format(m.time, 'DD.MM.YYYY HH:mm:ss');
    let content = m.text ? m.text : '';

    if (m.type === CHAT_MESSAGE_TYPE.PAM_REQUEST) {
      const getSubtitle = () => {
        const { requestType, appointmentAction, diagnosisRequestType } = m.data;

        switch (requestType) {
          case RequestTypes.APPOINTMENT:
            return `; ${t(`PamRequest.request_subtitle.appointment.${appointmentAction}`, {
              ns: 'components',
            })}`;
          case RequestTypes.FINDINGS_INQUIRY:
            return `; ${t(`PamRequest.request_subtitle.findings_inquiry.${diagnosisRequestType}`, {
              ns: 'components',
            })}`;
          case RequestTypes.GENERAL_INQUIRY ||
            RequestTypes.REFERRAL ||
            RequestTypes.REQUEST_FOLLOW_UP_PRESCRIPTION:
          default:
            return '';
        }
      };

      const subtitle = getSubtitle();
      content = `${t(`PamRequest.request_title.${m.data.requestType}`, {
        ns: 'components',
      })}${subtitle}`;
    }

    if (m.files && m.files.length > 0) {
      content += concatFileNames(m.files);
    }

    if (m.endedAt && m.establishedAt) {
      const minutes = differenceInMinutes(m.endedAt, m.establishedAt);
      const seconds = differenceInSeconds(m.endedAt, m.establishedAt) % 60;

      content += t('useChatMessagesExport.text', {
        title: m.title && `"${m.title}" `,
        start: format(m.establishedAt, 'HH:mm'),
        end: format(m.endedAt, 'HH:mm'),
        allowHtml: true,
        ns: 'global',
      });
      if (minutes > 0) {
        content += `${t('useChatMessagesExport.minutes', { count: minutes, ns: 'global' })} `;
      }
      if (minutes > 0 && seconds > 0) content += t('useChatMessagesExport.and');
      if (seconds > 0) {
        content += t('useChatMessagesExport.seconds', { count: seconds, ns: 'global' });
      }
    }

    content = content.trim();

    msgsExported += `${time}, ${fullNameSender}:\n${content}\n\n`;
  });
  return msgsExported || '';
};

export default useChatMessagesExport;
